/**
 * Allocate actions — the canonical session lifecycle, shaped for allocation.
 *
 * An allocation is not a new kind of record: it is a session whose method
 * distributes a finite envelope across asks. So every call here goes through
 * the same stores and RPCs the Session app uses (Doc 31 first law), and the
 * result lands in `session_results` like any other run.
 */

import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { uniqueLines } from '@/lib/uniqueLines';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import type { PrioritizationSession } from '@/types/prioritization/session';
import { createSessionWithFeedback, type CreateSessionFlowResult, type CreateSessionFlowOptions } from '@/lib/sessions/createSessionFlow';
import { SESSION_APP_MODE_CONFIG } from '../session/sessionAppActions';

/**
 * The two allocation methods this app offers.
 *
 * Buy-a-Feature is the default because spending a budget is legible and
 * engaging for a room; Cost-of-Delay is the alternate when the decision needs
 * defensible economics more than participation.
 */
export const ALLOCATION_METHODS: Array<{
  id: string;
  name: string;
  blurb: string;
}> = [
  {
    id: 'buy-a-feature',
    name: 'Buy a Feature',
    blurb:
      'Everyone gets a share of the budget and spends it. Fast, engaging, and it forces real trade-offs.',
  },
  {
    id: 'cost-of-delay',
    name: 'Cost of Delay',
    blurb:
      'Rank by what waiting actually costs. Slower, but the strongest footing for an approval memo.',
  },
];

export function allocationMethodName(toolId: string | undefined): string {
  return ALLOCATION_METHODS.find((m) => m.id === toolId)?.name ?? 'Allocation';
}

/** The asks in an allocation, resolved to titles for display. */
export function allocationAsks(
  session: PrioritizationSession,
): Array<{ id: string; name: string }> {
  if (session.sessionContextMode === 'isolated') {
    return uniqueLines(session.isolatedItems ?? []).map((text) => ({ id: text, name: text }));
  }
  const { getItemById } = useItemsStore.getState();
  return (session.itemIds ?? []).map((id) => ({
    id,
    name: getItemById(id)?.title ?? id,
  }));
}

export interface CreateAllocationInput {
  /** What is being split — the question, in the owner's words. */
  name: string;
  /** The asks competing for it. */
  asks: string[];
  /** Chosen method id. */
  toolId: string;
  /** Total envelope, recorded so the split can be read back in real units. */
  envelopeTotal?: number;
  /** Free-text unit label ('$', 'headcount', 'points') — display only. */
  envelopeUnit?: string;
  facilitatorId: string;
  /** Shows the non-blocking "Created — note: …" toast for a warn-and-proceed gate. */
  toast?: CreateSessionFlowOptions['toast'];
}

/**
 * Create the allocation as a canonical session.
 *
 * The envelope is carried in `modeConfig` rather than a new column: it is
 * configuration for how this run is presented, not a governed fact, and Doc 31
 * forbids an App inventing schema.
 */
export async function createAllocation(
  input: CreateAllocationInput,
): Promise<CreateSessionFlowResult> {
  return createSessionWithFeedback({
    name: input.name.trim(),
    facilitatorId: input.facilitatorId,
    createdBy: input.facilitatorId,
    intent: 'rank_priorities',
    sessionContextMode: 'isolated',
    isolatedItems: input.asks,
    selectedToolId: input.toolId,
    toolConfig: {
      // Buy-a-Feature reads budget_percentage; the rest is display context the
      // Split screen reads back so the result speaks in the owner's units.
      budget_percentage: 0.4,
      ...(input.envelopeTotal ? { envelope_total: input.envelopeTotal } : {}),
      ...(input.envelopeUnit ? { envelope_unit: input.envelopeUnit } : {}),
    },
    // Its people answer through the Session app's join address, in the live room.
    modeConfig: SESSION_APP_MODE_CONFIG,
    workspaceId: getCurrentWorkspaceId() ?? undefined,
  }, { toast: input.toast });
}

/** Read the envelope back off a session, for the Split screen's readout. */
export function readEnvelope(session: PrioritizationSession): {
  total: number | null;
  unit: string;
} {
  const config = (session.toolConfig ?? {}) as Record<string, unknown>;
  const total = typeof config.envelope_total === 'number' ? config.envelope_total : null;
  const unit = typeof config.envelope_unit === 'string' ? config.envelope_unit : '';
  return { total, unit };
}

/**
 * Turn a ranking output into an allocation readout.
 *
 * Scores are shares: each ask's score over the total becomes its slice of the
 * envelope. When no envelope was recorded we still show relative share, because
 * "60% of the room's budget went here" is a real answer even without units.
 */
export function toAllocationSplit(
  session: PrioritizationSession,
  asks: Array<{ id: string; name: string }>,
): Array<{ id: string; name: string; share: number; amount: number | null }> {
  const ranking = session.rankingOutput;
  if (!ranking?.rankedItemIds?.length) return [];
  const scores = (ranking.scores ?? {}) as Record<string, { total?: number }>;
  const byId = new Map(asks.map((a) => [a.id, a.name]));

  const rows = ranking.rankedItemIds.map((id: string) => ({
    id,
    name: byId.get(id) ?? id,
    raw: scores[id]?.total ?? 0,
  }));
  const sum = rows.reduce((n, r) => n + r.raw, 0);
  const { total } = readEnvelope(session);

  return rows.map((r) => {
    const share = sum > 0 ? r.raw / sum : 0;
    return {
      id: r.id,
      name: r.name,
      share,
      amount: total != null ? Math.round(total * share) : null,
    };
  });
}
